<template>
  <div class="exam-management">
    <!-- 页面头部 -->
    <div class="page-header">
      <h2>考试管理</h2>
      <div class="header-actions">
        <el-button type="primary" icon="el-icon-refresh" @click="refreshData">刷新</el-button>
      </div>
    </div>

    <!-- 功能导航 -->
    <div class="function-nav">
      <el-tabs v-model="activeTab" @tab-click="handleTabClick">
        <el-tab-pane label="出题管理" name="creation">
          <ExamCreation ref="examCreation" :course-id="courseId" />
        </el-tab-pane>
        <el-tab-pane label="上传试卷" name="upload">
          <ExamUpload ref="examUpload" :course-id="courseId" />
        </el-tab-pane>
        <el-tab-pane label="阅卷管理" name="grading">
          <ExamGrading ref="examGrading" :course-id="courseId" />
        </el-tab-pane>
        <el-tab-pane label="归档" name="history">
          <ExamHistory ref="examHistory" :course-id="courseId" />
        </el-tab-pane>
      </el-tabs>
    </div>

    <!-- 统计概览 -->
    <div class="statistics-overview" v-if="showStatistics">
      <h3 class="statistics-title">考试统计概览</h3>
      <el-row :gutter="20">
        <el-col :span="6">
          <div class="stat-card">
            <div class="stat-icon">
              <i class="el-icon-document"></i>
            </div>
            <div class="stat-content">
              <div class="stat-number">{{ statistics.totalExams }}</div>
              <div class="stat-label">总考试数</div>
            </div>
          </div>
        </el-col>
        <el-col :span="6">
          <div class="stat-card">
            <div class="stat-icon">
              <i class="el-icon-upload"></i>
            </div>
            <div class="stat-content">
              <div class="stat-number">{{ statistics.uploadedExams }}</div>
              <div class="stat-label">已上传试卷</div>
            </div>
          </div>
        </el-col>
        <el-col :span="6">
          <div class="stat-card">
            <div class="stat-icon">
              <i class="el-icon-edit"></i>
            </div>
            <div class="stat-content">
              <div class="stat-number">{{ statistics.gradedExams }}</div>
              <div class="stat-label">已批阅考试</div>
            </div>
          </div>
        </el-col>
        <el-col :span="6">
          <div class="stat-card">
            <div class="stat-icon">
              <i class="el-icon-time"></i>
            </div>
            <div class="stat-content">
              <div class="stat-number">{{ statistics.historyExams }}</div>
              <div class="stat-label">归档</div>
            </div>
          </div>
        </el-col>
      </el-row>
    </div>
  </div>
</template>

<script>
import ExamCreation from './ExamCreation.vue'
import ExamUpload from './ExamUpload.vue'
import ExamGrading from './ExamGrading.vue'
import ExamHistory from './ExamHistory.vue'
import { examApi } from '@/api/exam'

export default {
  name: 'ExamManagement',
  components: {
    ExamCreation,
    ExamUpload,
    ExamGrading,
    ExamHistory
  },
  props: {
    courseId: {
      type: [String, Number],
      required: true
    }
  },
  data() {
    return {
      activeTab: 'creation',
      showStatistics: true,
      statisticsLoaded: false,
      statistics: {
        totalExams: 0,
        uploadedExams: 0,
        gradedExams: 0,
        historyExams: 0
      }
    }
  },
  mounted() {
    // 页面加载时立即加载考试管理相关数据
    this.loadExamManagementData()
  },
  methods: {
    // 加载考试管理相关数据
    async loadExamManagementData() {
      // 加载统计数据
      await this.loadStatistics()
      // 加载当前标签页的数据
      this.refreshCurrentTab()
    },

    // 加载统计数据
    async loadStatistics() {
      try {
        const response = await examApi.getExamStatistics(this.$route.params.id)
        this.statistics = response.data
        this.statisticsLoaded = true
      } catch (error) {
        console.error('加载统计数据失败:', error)
      }
    },

    // 处理标签页切换
    handleTabClick(tab) {
      this.activeTab = tab.name
      // 加载当前标签页的数据
      this.refreshCurrentTab()
    },

    // 刷新当前标签页数据
    refreshCurrentTab() {
      const tabRefs = {
        creation: 'examCreation',
        upload: 'examUpload',
        grading: 'examGrading',
        history: 'examHistory'
      }

      const refName = tabRefs[this.activeTab]
      if (refName && this.$refs[refName]) {
        const component = this.$refs[refName]
        // 调用对应组件的loadData方法（统一接口）
        if (component.loadData) {
          component.loadData()
        }
      }
    },

    // 刷新数据
    refreshData() {
      this.loadStatistics()
      this.refreshCurrentTab()
    }
  }
}
</script>

<style scoped>
.exam-management {
  padding: 20px;
}

.page-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}

.page-header h2 {
  margin: 0;
  color: #303133;
}

.header-actions {
  display: flex;
  gap: 10px;
}

.function-nav {
  margin-bottom: 20px;
}

.statistics-overview {
  margin-bottom: 20px;
}

.statistics-title {
  margin: 0 0 20px 0;
  color: #303133;
  font-size: 18px;
  font-weight: 600;
  border-left: 4px solid #409EFF;
  padding-left: 12px;
}

.stat-card {
  display: flex;
  align-items: center;
  padding: 20px;
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.1);
  transition: all 0.3s;
}

.stat-card:hover {
  box-shadow: 0 4px 20px 0 rgba(0, 0, 0, 0.15);
  transform: translateY(-2px);
}

.stat-icon {
  width: 60px;
  height: 60px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 20px;
  font-size: 24px;
  color: #fff;
}

.stat-card:nth-child(1) .stat-icon {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
}

.stat-card:nth-child(2) .stat-icon {
  background: linear-gradient(135deg, #f093fb 0%, #f5576c 100%);
}

.stat-card:nth-child(3) .stat-icon {
  background: linear-gradient(135deg, #4facfe 0%, #00f2fe 100%);
}

.stat-card:nth-child(4) .stat-icon {
  background: linear-gradient(135deg, #43e97b 0%, #38f9d7 100%);
}

.stat-content {
  flex: 1;
}

.stat-number {
  font-size: 28px;
  font-weight: bold;
  color: #303133;
  margin-bottom: 5px;
}

.stat-label {
  font-size: 14px;
  color: #909399;
}

/* 标签页样式优化 */
.function-nav .el-tabs__header {
  margin-bottom: 0;
}

.function-nav .el-tabs__content {
  padding: 20px 0;
}

.function-nav .el-tab-pane {
  min-height: 600px;
}
</style>
